import { Star, Info } from 'lucide-react';
import { Agent } from '../../../types/agent';
import { AgentAvatar } from './AgentAvatar';
import { SCOPE_META } from '../utils/agentVisuals';

interface AgentCardProps {
  agent: Agent;
  isActive: boolean;
  onSelect: (agent: Agent) => void;
  onTogglePin?: (agent: Agent) => void;
  onInfo?: (agent: Agent) => void;
  showUsage?: boolean;
  variant?: 'home' | 'catalog';
}

export const AgentCard = ({
  agent,
  isActive,
  onSelect,
  onTogglePin,
  onInfo,
  showUsage = true,
  variant = 'catalog',
}: AgentCardProps) => {
  const scope = SCOPE_META[agent.visibility_level];

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(agent)}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect(agent);
        }
      }}
      aria-label={`Selecionar ${agent.name}`}
      className={`
        w-full text-left bg-[#1a1a1a] border rounded-xl p-3.5
        flex flex-col gap-2 transition-all cursor-pointer
        ${isActive
          ? 'border-[#F4CC04] bg-[#F4CC04]/5'
          : 'border-[#333333] hover:border-[#F4CC04]/60'
        }
      `}
    >
      <div className="flex items-center gap-2">
        <AgentAvatar agent={agent} size={26} />
        <span className="text-white font-semibold text-sm truncate flex-1">{agent.name}</span>
        {isActive && (
          <span className="text-[10px] font-semibold text-black bg-[#F4CC04] rounded px-1.5 py-0.5 flex-shrink-0">
            ATIVO
          </span>
        )}
        {onInfo && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onInfo(agent); }}
            className="text-[#888888] hover:text-[#F4CC04] flex-shrink-0"
            aria-label={`Detalhes de ${agent.name}`}
          >
            <Info size={15} />
          </button>
        )}
        {onTogglePin && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onTogglePin(agent); }}
            className={`flex-shrink-0 transition-colors ${
              agent.pinned ? 'text-[#F4CC04]' : 'text-[#555555] hover:text-[#F4CC04]'
            }`}
            aria-label={agent.pinned ? `Desfixar ${agent.name}` : `Fixar ${agent.name}`}
          >
            <Star size={15} fill={agent.pinned ? 'currentColor' : 'none'} />
          </button>
        )}
      </div>

      {agent.description && (
        <p className="text-[#888888] text-xs leading-relaxed line-clamp-2">{agent.description}</p>
      )}

      {variant === 'catalog' && (
        <div className="flex items-center gap-2 mt-auto">
          {scope && (
            <span
              className="text-[9px] font-semibold rounded px-1.5 py-0.5"
              style={{ color: scope.fg, background: scope.bg }}
            >
              {scope.label}
            </span>
          )}
          {showUsage && agent.uses_this_month > 0 && (
            <span className="text-[#666666] text-[10px]">
              usado {agent.uses_this_month}× este mês
            </span>
          )}
        </div>
      )}
    </div>
  );
};
